import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listSerials } from "@/lib/inventory-views.functions";
import { useScope } from "@/hooks/use-scope";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/ui/page-header";
import { ExportMenu } from "@/components/ui/export-menu";
import { FilterBar } from "@/components/ui/filter-bar";
import { DataTable, type ColumnDef } from "@/components/ui/data-table";
import { StatusPill, statusTone } from "@/components/ui/status-pill";
import { ScanInput } from "@/components/ui/scan-input";
import { Hash } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/inventory/serials")({ component: SerialsPage });

const STATUSES = ["all", "received", "on_hand", "allocated", "picked", "shipped", "returned", "scrapped"] as const;

type SerialRow = {
  id: string; serial_value: string; status: string; lot: string | null;
  created_at: string; sku_id: string; location_id: string | null;
};

function SerialsPage() {
  const { tenantId, customerId } = useScope();
  const listSerialsFn = useServerFn(listSerials);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<typeof STATUSES[number]>("all");

  const { data: bundle, isLoading } = useQuery({
    queryKey: ["serials", tenantId, customerId, status],
    enabled: !!tenantId,
    queryFn: () => listSerialsFn({ data: {
      tenantId: tenantId!, customerId: customerId ?? null, status,
    } }),
    placeholderData: keepPreviousData,
  });
  const data = (bundle?.rows ?? []) as SerialRow[];
  const skus = bundle?.skus ?? [];

  const skuMap = useMemo(() => Object.fromEntries(skus.map((s) => [s.id, s])), [skus]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((s) => {
      const sku = skuMap[s.sku_id];
      return s.serial_value.toLowerCase().includes(q)
        || (sku?.sku_code?.toLowerCase().includes(q) ?? false)
        || (sku?.name?.toLowerCase().includes(q) ?? false);
    });
  }, [data, search, skuMap]);

  const onScan = (code: string) => {
    const hit = data.find((s) => s.serial_value.toLowerCase() === code.trim().toLowerCase());
    if (hit) setSearch(hit.serial_value);
    else toast.error(`No serial "${code}" in current scope`);
  };

  const cols: ColumnDef<SerialRow, unknown>[] = [
    { id: "serial", header: "Serial", accessorKey: "serial_value", cell: ({ row }) => <span className="font-mono text-xs">{row.original.serial_value}</span> },
    { id: "sku", header: "SKU", accessorFn: (r) => skuMap[r.sku_id]?.sku_code ?? "", cell: ({ row }) => {
      const s = skuMap[row.original.sku_id];
      return s ? <div><div className="font-mono text-xs">{s.sku_code}</div><div className="text-[11px] text-muted-foreground truncate max-w-[180px]">{s.name}</div></div> : <span className="text-muted-foreground">—</span>;
    } },
    { id: "lot", header: "Lot", cell: ({ row }) => <span className="text-xs text-muted-foreground font-mono">{row.original.lot ?? "—"}</span> },
    { id: "status", header: "Status", accessorKey: "status", cell: ({ row }) => <StatusPill tone={statusTone(row.original.status)} dot>{row.original.status}</StatusPill> },
    { id: "created", header: "Created", accessorKey: "created_at", cell: ({ row }) => <span className="text-xs text-muted-foreground">{new Date(row.original.created_at).toLocaleString()}</span> },
  ];

  return (
    <>
      <PageHeader
        title="Serial Numbers"
        description="Tracked unit-level inventory across the lifecycle."
        meta={<span className="text-xs text-muted-foreground">{filtered.length} of {data.length} shown</span>}
        actions={tenantId ? (
          <ExportMenu
            entity="serials"
            tenantId={tenantId}
            customerId={customerId}
            getRows={() => filtered.map((s) => {
              const sku = skuMap[s.sku_id];
              return {
                serial_value: s.serial_value,
                sku_code: sku?.sku_code ?? "",
                sku_name: sku?.name ?? "",
                lot: s.lot ?? "",
                status: s.status,
                created_at: s.created_at,
              };
            })}
            columns={["serial_value","sku_code","sku_name","lot","status","created_at"]}
            filters={{ status, search }}
            disabled={filtered.length === 0}
          />
        ) : null}
      />
      <div className="px-4 sm:px-6 py-4 space-y-4">
        <ScanInput onScan={onScan} placeholder="Scan a serial number to locate…" />
        <FilterBar search={search} onSearchChange={setSearch} searchPlaceholder="Search serial, SKU code or name…">
          <Select value={status} onValueChange={(v) => setStatus(v as typeof STATUSES[number])}>
            <SelectTrigger className="w-44 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All statuses" : s}</SelectItem>)}</SelectContent>
          </Select>
        </FilterBar>
        <DataTable
          data={filtered}
          columns={cols}
          isLoading={isLoading}
          getRowId={(r) => r.id}
          empty={{ title: data.length > 0 ? "No matches" : "No serials yet", description: data.length > 0 ? "Try adjusting search or status filter." : "Receive serial-tracked inventory to populate this list." }}
          mobileCard={(s) => {
            const sku = skuMap[s.sku_id];
            return (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-sm flex items-center gap-1.5"><Hash className="h-3 w-3 text-muted-foreground" />{s.serial_value}</span>
                  <StatusPill tone={statusTone(s.status)} dot>{s.status}</StatusPill>
                </div>
                <div className="text-xs text-muted-foreground truncate">{sku ? `${sku.sku_code} — ${sku.name}` : "—"}</div>
                <div className="text-[11px] text-muted-foreground">{s.lot ? `Lot ${s.lot} · ` : ""}{new Date(s.created_at).toLocaleDateString()}</div>
              </div>
            );
          }}
        />
      </div>
    </>
  );
}
